// Import necessary modules and types from refinedev and zustand
import {UseDialogStateReturn} from '@/hooks/useDialogState';
import {isShallowEqual} from '@/lib/utils';
import {BaseKey} from '@refinedev/core';
import {Edge, Node, Viewport} from '@xyflow/react';
import {create} from 'zustand';
import {auto_calculate_dates} from './utils/auto_calculate_dates';

export type TimelineMarker = {
  id: string;
  label: string;
  x: number;
  width: number;
  timestamp?: Date;
  extra?: any;
};

// Define a function type for state updates
type FnSet<T> = (value: T) => T;

type ChangeType = 'modified' | 'new' | 'deleted';

type WorkflowChangeData = {
  nodes: {[key: string]: ChangeType};
  edges: {[key: string]: ChangeType};
};

// State snapshot for history
type WorkflowSnapshot = {
  nodes: Node[];
  edges: Edge[];
  changes: WorkflowChangeData;
  swimlanes: any[];
  timelineMarkers: TimelineMarker[];
};

// Define the options type
type WorkflowOptions = {
  show_details: boolean;
  show_timelines: boolean;
};

// Maximum number of history entries to keep
const MAX_HISTORY_DEPTH = 30;

// Define the WorkflowStore interface for state management in the workflow module.
export type WorkflowStore = {
  clearData: () => void;
  project_id: BaseKey;
  // Setter for project_id. It updates the current project ID
  setProjectId: (project_id: BaseKey) => void;
  project_data: any;
  setProjectData: (project_data: any) => void;

  task_id?: BaseKey;
  setTaskId: (task_id: BaseKey) => void; //this workflow belong to a task?

  no_node_tasks: any[];
  // Setter for no_node_tasks. It updates the list of tasks without nodes.
  setNoNodeTasks: (tasks: any[]) => void;
  nodes: any[];
  // Setter for nodes. Accepts either an array or a function to update the current nodes.
  setNodes: (nodes: any[] | FnSet<any[]>) => void;
  edges: any[];
  // Setter for edges. Accepts either an array or a function to update the current edges.
  setEdges: (edges: any[] | FnSet<any[]>) => void;
  viewport: any;
  // Setter for viewport. Updates the view settings.
  setViewport: (viewport: any) => void;
  swimlanes: any[];
  // Setter for swimlanes. Accepts an array or a function for updating swimlane items.
  setSwimlanes: (swimlanes: any[] | FnSet<any[]>) => void;
  timelineMarkers: any[];
  // Setter for timelineMarkers. Accepts an array or a function to update timeline marker items.
  setTimelineMarkers: (markers: TimelineMarker[] | FnSet<TimelineMarker[]>) => void;
  /// Changes state to track dirty objects
  changes: WorkflowChangeData;
  // Setter for changes. Accepts an array or a function to update the dirty objects.
  setChange: (obj: string, id: string, dirty: ChangeType) => void;
  clearChanges: () => void;

  recalculateTaskNodeDates: () => void;

  // History management for undo/redo
  history: WorkflowSnapshot[];
  historyIndex: number;
  canUndo: boolean;
  canRedo: boolean;
  maxHistoryDepth: number;
  setMaxHistoryDepth: (depth: number) => void;
  takeSnapshot: () => void;
  undo: () => void;
  redo: () => void;

  // dialog states
  dialogState: {
    createNode?: UseDialogStateReturn;
    editNode?: UseDialogStateReturn;
  };
  setDialogState: (type: 'createNode' | 'editNode', state: UseDialogStateReturn) => void;

  selectedNodes: Node[];
  setSelectedNodes: (nodes: Node[]) => void;
  selectedEdges: Edge[];
  setSelectedEdges: (edges: Edge[]) => void;

  // Options for workflow display
  options: WorkflowOptions;
  setOptions: (options: Partial<WorkflowOptions>) => void;
};

const useWorkflowStore = create<WorkflowStore>()(set => ({
  clearData() {
    set({
      task_id: undefined,
      project_id: '',
      project_data: null,
      no_node_tasks: [],
      nodes: [],
      edges: [],
      viewport: {x: 0, y: 0, zoom: 1},
      swimlanes: [],
      timelineMarkers: [],
      changes: {nodes: {}, edges: {}},
      history: [],
      historyIndex: -1,
      canUndo: false,
      canRedo: false,
      selectedNodes: [],
      selectedEdges: [],
      options: {show_details: false, show_timelines: true},
    });
  },
  project_id: '',
  // Updates the project_id state.
  setProjectId: (project_id: BaseKey) => set({project_id}),
  project_data: null,
  // Updates the project_data state.
  setProjectData: (project_data: any) => set({project_data}),

  // task_id is optional, it is used to determine if the workflow is for a task
  task_id: undefined,
  setTaskId: (task_id: BaseKey) => set({task_id}),

  no_node_tasks: [],
  // Updates the no_node_tasks state.
  setNoNodeTasks: (no_node_tasks: any[]) => set({no_node_tasks}),
  nodes: [],
  // Updates the nodes state. If a function is provided, compute the next state.
  setNodes: (nodes: any[] | FnSet<any[]>) => {
    if (typeof nodes === 'function') {
      set(state => {
        const newNodes = nodes(state.nodes);
        const changes = {...state.changes};
        // Mark changed or new nodes as dirty
        newNodes.forEach(node => {
          const existingNode = state.nodes.find(n => n.id === node.id);
          if (!existingNode) changes.nodes[node.id] = 'new';
          else if (!isShallowEqual(existingNode.data, node.data))
            changes.nodes[node.id] = 'modified';
        });
        return {nodes: newNodes, changes};
      });
    } else
      set(state => {
        const changes = {...state.changes};
        // Mark all new nodes as dirty
        nodes.forEach(node => {
          const existingNode = state.nodes.find(n => n.id === node.id);
          if (!existingNode) changes.nodes[node.id] = 'new';
          else if (!isShallowEqual(existingNode.data, node.data))
            changes.nodes[node.id] = 'modified';
        });
        return {nodes, changes};
      });
  },
  edges: [],
  // Updates the edges state. If a function is provided, compute the next state.
  setEdges: (edges: any[] | FnSet<any[]>) => {
    if (typeof edges === 'function') {
      set(state => ({edges: edges(state.edges)}));
    } else set({edges});
  },
  viewport: {x: 0, y: 0, zoom: 1},
  // Updates the viewport state with new view settings.
  setViewport: (viewport: any) => set({viewport}),
  swimlanes: [],
  // Updates the swimlanes state. If a function is provided, compute the next state.
  setSwimlanes: (swimlanes: any[] | FnSet<any[]>) => {
    if (typeof swimlanes === 'function') {
      set(state => ({swimlanes: swimlanes(state.swimlanes)}));
    } else set({swimlanes});
  },
  timelineMarkers: [],
  // Updates the timelineMarkers state. If a function is provided, compute the next state.
  setTimelineMarkers: (markers: TimelineMarker[] | FnSet<TimelineMarker[]>) => {
    if (typeof markers === 'function') {
      set(state => ({timelineMarkers: markers(state.timelineMarkers)}));
    } else set({timelineMarkers: markers});
  },
  changes: {nodes: {}, edges: {}},
  // Updates the changes state. If a function is provided, compute the next state.
  setChange: (obj: string, id: string, dirty: ChangeType) => {
    set(state => {
      const changes = {...state.changes};
      changes[obj][id] = dirty;
      return {changes};
    });
  },
  clearChanges: () => set({changes: {nodes: {}, edges: {}}}),
  recalculateTaskNodeDates() {
    set(state => {
      const start_node = state.nodes.find(n => n.type === 'start');
      if (!start_node) return state;
      const {newNodes} = auto_calculate_dates(start_node, state.nodes, state.edges);
      return {nodes: newNodes};
    });
  },

  // History management for undo/redo
  history: [],
  historyIndex: -1,
  canUndo: false,
  canRedo: false,
  maxHistoryDepth: MAX_HISTORY_DEPTH,

  setMaxHistoryDepth: (maxHistoryDepth: number) => set({maxHistoryDepth}),

  takeSnapshot: () => {
    set(state => {
      const currentSnapshot: WorkflowSnapshot = {
        nodes: [...state.nodes],
        edges: [...state.edges],
        changes: {nodes: {...state.changes.nodes}, edges: {...state.changes.edges}},
        swimlanes: [...state.swimlanes],
        timelineMarkers: [...state.timelineMarkers],
      };
      console.log(`takeSnapshot, current history = ${state.history.length}`);

      // Remove future history if we've gone back in time and made changes
      const newHistory =
        state.historyIndex < state.history.length - 1
          ? state.history.slice(0, state.historyIndex + 1)
          : [...state.history];

      // Add current state to history
      newHistory.push(currentSnapshot);

      // Trim history if it exceeds the maximum depth
      const trimmedHistory =
        newHistory.length > state.maxHistoryDepth
          ? newHistory.slice(newHistory.length - state.maxHistoryDepth)
          : newHistory;

      // Adjust history index after trimming
      const newIndex = trimmedHistory.length - 1;

      // Update history and index
      return {
        history: trimmedHistory,
        historyIndex: newIndex,
        canUndo: trimmedHistory.length > 1,
        canRedo: false,
      };
    });
  },

  undo: () => {
    set(state => {
      if (state.historyIndex <= 0) {
        return {canUndo: false};
      }

      const newIndex = state.historyIndex - 1;
      const snapshot = state.history[newIndex];

      return {
        nodes: [...snapshot.nodes],
        edges: [...snapshot.edges],
        changes: {...snapshot.changes},
        swimlanes: [...snapshot.swimlanes],
        timelineMarkers: [...snapshot.timelineMarkers],
        historyIndex: newIndex,
        canUndo: newIndex > 0,
        canRedo: true,
      };
    });
  },

  redo: () => {
    set(state => {
      if (state.historyIndex >= state.history.length - 1) {
        return {canRedo: false};
      }

      const newIndex = state.historyIndex + 1;
      const snapshot = state.history[newIndex];

      return {
        nodes: [...snapshot.nodes],
        edges: [...snapshot.edges],
        changes: {...snapshot.changes},
        swimlanes: [...snapshot.swimlanes],
        timelineMarkers: [...snapshot.timelineMarkers],
        historyIndex: newIndex,
        canUndo: true,
        canRedo: newIndex < state.history.length - 1,
        // Mark everything as modified on redo
        // changes: {
        //   nodes: snapshot.nodes.reduce((acc, node) => {
        //     acc[node.id] = 'modified';
        //     return acc;
        //   }, {} as {[key: string]: ChangeType}),
        //   edges: snapshot.edges.reduce((acc, edge) => {
        //     acc[edge.id] = 'modified';
        //     return acc;
        //   }, {} as {[key: string]: ChangeType})
        // }
      };
    });
  },

  dialogState: {
    createNode: undefined,
    editNode: undefined,
  },
  setDialogState: (type: 'createNode' | 'editNode', state: UseDialogStateReturn) =>
    set(currentState => ({
      dialogState: {
        ...currentState.dialogState,
        [type]: state,
      },
    })),

  selectedNodes: [],
  setSelectedNodes: (selectedNodes: Node[]) => set({selectedNodes}),
  selectedEdges: [],
  setSelectedEdges: (selectedEdges: Edge[]) => set({selectedEdges}),

  // Initialize options and define the setter
  options: {show_details: false, show_timelines: false},
  setOptions: (options: Partial<WorkflowOptions>) =>
    set(state => ({
      options: {...state.options, ...options},
    })),
}));

export default useWorkflowStore;
